<template>
    <div class="container-fluid">
        <nav>
            <ol class="breadcrumb">
                <li class="breadcrumb-item"><router-link to="/mypage">マイページ</router-link></li>
                <li class="breadcrumb-item"><router-link :to="booksPath">小説管理</router-link></li>
                <li class="breadcrumb-item" v-if="book.id"><router-link :to="`/mypage/book/${book.id}/sections/`">本文一覧</router-link></li>
                <li class="breadcrumb-item">小説編集</li>
            </ol>
        </nav>
        <div class="container">
            <legend>
                <span v-if="!book.id">小説の新規追加</span>
                <span v-else>小説情報の編集</span>
            </legend>
            <div class="text-muted small" v-if="!book.id">
                小説情報を登録します。
                投稿する小説のタイトル、紹介文、長さを選んで、本文を執筆してください。
                <div class="text-danger" v-if="!hasDomain">
                    サブドメインを取得せずに小説を投稿すると、小説はポータルにしか表示されません。
                </div>
            </div>
            <div class="mb-3">
                <div class="row">
                    <div class="col-6">
                        <label class="caption no-border">
                            タイトル
                            <span class="badge badge-pill badge-danger">必須</span>
                        </label>
                    </div>
                    <div class="col-6 text-right" :class="{'text-danger': book.title.length > 80}">
                        <div class="chars">
                            ({{book.title.length}} / 80文字)
                        </div>
                    </div>
                </div>
                <input type="text" class="form-control" :class="{'is-invalid': errors.title}" v-model="book.title" placeholder="例）街角の貸し本棚" />
                <span class="invalid-feedback" role="alert" v-if="errors.title">
                    <strong>{{ errors.title[0] }}</strong>
                </span>
            </div>
            <div class="mb-3">
                <div class="row">
                    <div class="col-6">
                        <label class="caption no-border">サブタイトル</label>
                    </div>
                    <div class="col-6 text-right">
                        <div class="chars">
                            ({{subtitleLength}} / 80文字)
                        </div>
                    </div>
                </div>
                <input type="text" class="form-control" v-model="book.subtitle" placeholder="例）〜本を置くたびに幸せがひとつふえていく〜" />
            </div>
            <div class="mb-3">
                <label class="caption no-border">
                    紹介文
                    <span class="badge badge-pill badge-danger">必須</span>
                </label>
                <div class="text-pane" :class="{'is-invalid': errors.description}">
                    <div class="text-pane-dummy">{{book.description + '\u200b'}}</div>
                    <textarea class="text-pane-editor"
                        v-model="book.description"
                        placeholder="作品の紹介文、概要、あらすじ。"></textarea>
                </div>
                <span class="invalid-feedback" role="alert" v-if="errors.description">
                    <strong>{{ errors.description[0] }}</strong>
                </span>
            </div>
            <div class="mb-3">
                <label class="caption">
                    長編・短編
                    <span class="badge badge-pill badge-danger">必須</span>
                </label>
                <div :class="{'is-invalid': errors.is_long}">
                    <label class="btn btn-radio" :class="{
                        'btn-primary': book.is_long == 1,
                        'btn-outline-primary': book.is_long != 1,
                    }">
                        <input type="radio" v-model="book.is_long" :value="1" class="d-none" />
                        <radio :value="book.is_long === 1"></radio>
                        長編
                    </label>
                    <label class="btn btn-radio" :class="{
                        'btn-secondary': book.is_long == 0,
                        'btn-outline-secondary': book.is_long != 0,
                        'disabled': !isShortable
                    }">
                        <input type="radio" v-model="book.is_long" :value="0" :disabled="!isShortable" class="d-none" />
                        <radio :value="book.is_long === 0"></radio>
                        短編
                    </label>
                </div>
                <span class="invalid-feedback" role="alert" v-if="errors.is_long">
                    <strong>{{ errors.is_long[0] }}</strong>
                </span>
                <div class="small">
                    <div>
                        短編…文字数に関わらず、本文を１話しか追加できません。１話完結となります。目次が表示されません。
                    </div>
                    <div>
                        長編…文字数に関わらず、本文を複数話追加できます。
                    </div>
                </div>
            </div>
            <div>
                <a href="#" @click.prevent="details = !details">
                    <i class="fas fa-chevron-right" v-if="!details"></i>
                    <i class="fas fa-chevron-down" v-else></i>
                    ジャンル・タグ・レーティング・表紙
                </a>
            </div>
            <div v-show="details">
                <div class="mb-3">
                    <label class="caption">
                        タグ
                        <span :class="{'text-danger': book.tags.length >= 15}">({{book.tags.length}} / 15)</span>
                    </label>
                    <div>
                        <span class="badge badge-success badge-pill badge-tag" v-for="(v, i) in book.tags" :key="v.id">
                            {{v.tag_name}}
                            <button type="button" class="close" @click="removeTag(i)">
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </span>
                    </div>
                    <div class="input-group">
                        <input type="text" class="form-control" style="height:41px" v-model="tag" placeholder="新規タグ (Enterで追加)" @keydown.enter="addTag" />
                        <div class="input-group-append">
                            <button type="button" class="btn btn-secondary" @click="addTag()">
                                <i class="fas fa-plus"></i>
                                追加 (Enter)
                            </button>
                        </div>
                    </div>
                    <div v-if="mytags.length" class="mt-2">
                        <div>過去に使用したタグ</div>
                        <button type="button" class="btn btn-light btn-sm mr-1" v-for="t in mytags" :key="t.id" @click="appendTag(t)">
                            {{t.tag_name}}
                        </button>
                    </div>
                    <div class="text-muted small mt-2">
                        ボーイズラブ要素を含む作品には「BL」タグ、ガールズラブ要素を含む作品には「GL」タグ、
                        その他注意が必要な要素があれば、適切なタグを付与してください。
                    </div>
                </div>
                <div class="mb-3">
                    <label class="caption">
                        セルフレーティング
                        <i class="fas fa-lock" v-if="book.override_at" title="管理者によって変更がロックされています"></i>
                        <span class="badge badge-danger" v-if="overRated">
                            <i class="fas fa-exclamation-triangle"></i>
                            許可レーティングを超えています。
                        </span>
                    </label>
                    <div>
                        <label class="btn btn-outline-secondary btn-radio" :class="{
                            active: book.rating_id === null,
                            disabled: book.override_at
                        }">
                            <input type="radio" v-model="book.rating_id" :value="null" :disabled="book.override_at" class="d-none" />
                            <radio :value="book.rating_id === null"></radio>
                            無し
                        </label>
                        <label v-for="v in ratings" :key="v.id" class="btn btn-radio"
                            :class="{
                                active: book.rating_id == v.id,
                                disabled: book.override_at,
                                'btn-outline-warning': v.color == 'warning',
                                'btn-outline-danger': v.color == 'danger',
                                'btn-outline-dark': v.color == 'dark',
                            }">
                            <input type="radio" v-model="book.rating_id" :value="v.id" :disabled="book.override_at" class="d-none" />
                            <radio :value="book.rating_id === v.id"></radio>
                            {{v.rating_name}}
                        </label>
                    </div>
                    <div class="small">
                        <div v-for="v in ratings" :key="v.id">
                            <span :class="{'strong': book.rating_id == v.id}">{{v.rating_name}}…{{v.description}}</span>
                        </div>
                        <div v-if="overRated" class="small text-danger">
                            ユーザーの許可レーティングを超えています。
                            作品を公開することはできますが、ご自身でご自身の作品を閲覧することができなくなります。
                            <router-link to="/mypage/account">アカウント情報</router-link> で許可レーティングを確認してください。
                        </div>
                    </div>
                </div>
                <div class="mb-3" v-if="!$store.getters.isGuest">
                    <label class="caption">
                        公開範囲
                    </label>
                    <div>
                        <label class="btn btn-radio" :class="{
                            'btn-primary': book.is_hidden == 0,
                            'btn-outline-primary': book.is_hidden != 0,
                        }">
                            <input type="radio" v-model="book.is_hidden" :value="0" class="d-none" />
                            <radio :value="!book.is_hidden"></radio>
                            <i class="fas fa-eye"></i>
                            公開する
                        </label>
                        <label class="btn btn-radio" :class="{
                            'btn-secondary': book.is_hidden == 1,
                            'btn-outline-secondary': book.is_hidden != 1,
                            'disabled': !hasDomain
                        }">
                            <input type="radio" v-model="book.is_hidden" :value="1" class="d-none" :disabled="!hasDomain" />
                            <radio :value="book.is_hidden == 1"></radio>
                            <i class="fas fa-eye-slash"></i>
                            本棚専用
                        </label>
                        <label class="btn btn-radio" :class="{
                            'btn-warning': book.is_hidden == 2,
                            'btn-outline-warning': book.is_hidden != 2,
                        }">
                            <input type="radio" v-model="book.is_hidden" :value="2" class="d-none" />
                            <radio :value="book.is_hidden == 2"></radio>
                            <i class="fas fa-eye-slash"></i>
                            非公開
                        </label>
                        <label class="btn btn-radio" :class="{
                            'btn-danger': book.is_hidden == 3,
                            'btn-outline-danger': book.is_hidden != 3,
                        }">
                            <input type="radio" v-model="book.is_hidden" :value="3" class="d-none" />
                            <radio :value="book.is_hidden == 3"></radio>
                            <i class="fas fa-eye-slash"></i>
                            保留
                        </label>
                    </div>
                    <div class="text-muted small">
                        <div :class="{strong: book.is_hidden === 0}">公開する…ポータル○、本棚○、URL直○。</div>
                        <div :class="{strong: book.is_hidden === 1}">本棚専用…ポータル×、本棚○、URL直○。</div>
                        <div :class="{strong: book.is_hidden === 2}">
                            非公開…
                            ポータル×、本棚×、URL直○。
                            公開前の事前確認や、URL共有による限定公開などにお使いいただけます。
                        </div>
                        <div :class="{strong: book.is_hidden === 3}" class="text-danger">
                            保留…
                            ポータル×、本棚×、URL直×。
                            公開日を設定しても表示されないのでご注意ください。
                        </div>
                        <div class="text-danger font-weight-bold" v-if="book.novels && book.novels.length && book.is_hidden > 0">
                            投稿サイトと同期中の作品を非公開にすると、同期設定が削除されます。
                        </div>
                    </div>
                </div>
                <div class="mb-3">
                    <label class="caption">
                        この小説のジャンル
                    </label>
                    <div>
                        <label class="btn btn-light btn-radio" :class="{active: book.genre_id == null}">
                            <input type="radio" v-model="book.genre_id" :value="null" class="d-none" />
                            <radio :value="book.genre_id === null"></radio>
                            無し
                        </label>
                        <label v-for="v in genres" :key="v.id" class="btn btn-light btn-radio" :class="{active: book.genre_id == v.id}">
                            <input type="radio" v-model="book.genre_id" :value="v.id" class="d-none" />
                            <radio :value="book.genre_id === v.id"></radio>
                            {{v.genre_name}}
                        </label>
                    </div>
                    <div class="small description" v-if="selectedGenre">
                        {{selectedGenre}}
                    </div>
                    <div class="text-muted small">
                        ジャンルは本棚の中でタブで分類されます。
                        最大５ジャンルまで追加できます。
                        ジャンルはマイページのジャンル編集で追加・変更できます。
                    </div>
                </div>
                <div class="mb-3">
                    <label class="caption">
                        シリーズ
                    </label>
                    <div>
                        <label class="btn btn-light btn-radio" :class="{active: book.series_id == null}">
                            <input type="radio" v-model="book.series_id" :value="null" class="d-none" />
                            <radio :value="book.series_id === null"></radio>
                            無し
                        </label>
                        <label v-for="v in series" :key="v.id" class="btn btn-light btn-radio" :class="{active: book.series_id == v.id}">
                            <input type="radio" v-model="book.series_id" :value="v.id" class="d-none" />
                            <radio :value="book.series_id === v.id"></radio>
                            {{v.series_name}}
                            <i class="fas fa-layer-group" v-if="v.is_group"></i>
                        </label>
                    </div>
                    <div class="small description" v-if="selectedSeries">
                        {{selectedSeries}}
                    </div>
                    <div class="text-muted small">
                        ジャンルは本棚の中でタブで分類されます。
                        最大５ジャンルまで追加できます。
                        ジャンルはマイページのジャンル編集で追加・変更できます。
                    </div>
                </div>
                <div class="mb-3">
                    <label class="caption">
                        メールバックアップ
                    </label>
                    <div>
                        <label class="btn btn-radio" :class="{
                            'btn-secondary': book.is_backup == 0,
                            'btn-outline-secondary': book.is_backup != 0,
                        }">
                            <input type="radio" v-model="book.is_backup" :value="0" class="d-none" />
                            <radio :value="book.is_backup === 0"></radio>
                            <i class="fas fa-ban"></i>
                            バックアップしない
                        </label>
                        <label class="btn btn-radio" :class="{
                            'btn-primary': book.is_backup == 1,
                            'btn-outline-primary': book.is_backup != 1,
                            'disabled': !hasVerifiedEmail
                        }">
                            <input type="radio" v-model="book.is_backup" :value="1" class="d-none" :disabled="!hasVerifiedEmail" />
                            <radio :value="book.is_backup === 1"></radio>
                            <i class="fas fa-envelope-open-text"></i>
                            バックアップする
                        </label>
                    </div>
                    <div class="text-muted small">
                        24時間以内に小説情報、あるいは本文が更新された場合、1日1回作品のZIPアーカイブをご登録メールアドレスに送信します。<br>
                        登録メールアドレスがRFC822に非準拠の形式である場合、メールは送信されません。
                    </div>
                </div>
                <div class="mb-3">
                    <label class="caption">
                        OGPカード
                    </label>
                    <div>
                        <label class="btn btn-radio" :class="{
                            'btn-primary': book.card_type == 0,
                            'btn-outline-primary': book.card_type != 0,
                        }">
                            <input type="radio" v-model="book.card_type" :value="0" class="d-none" />
                            <radio :value="book.card_type === 0"></radio>
                            <i class="fas fa-ban"></i>
                            表紙絵か文字
                        </label>
                        <label class="btn btn-radio" :class="{
                            'btn-secondary': book.card_type == 1,
                            'btn-outline-secondary': book.card_type != 1,
                        }">
                            <input type="radio" v-model="book.card_type" :value="1" class="d-none" />
                            <radio :value="book.card_type === 1"></radio>
                            <i class="fas fa-envelope-open-text"></i>
                            文字カード（白）
                        </label>
                        <label class="btn btn-radio" :class="{
                            'btn-dark': book.card_type == 2,
                            'btn-outline-dark': book.card_type != 2,
                        }">
                            <input type="radio" v-model="book.card_type" :value="2" class="d-none" />
                            <radio :value="book.card_type === 2"></radio>
                            <i class="fas fa-envelope-open-text"></i>
                            文字カード（黒）
                        </label>
                    </div>
                    <div v-if="book.id" class="my-3">
                        <img :src="twitterCard" class="img-thumbnail twitter-img" />
                    </div>
                    <div class="text-muted small">
                        OGPカードの種類を指定します。
                        「表紙絵か文字」の場合、表紙絵があれば表紙絵を、なければ文字カード（白）を表示します。<br>
                        blueskyやXなどのOGP対応サイトに小説のURLを投稿すると、上記画像を表示します。
                        サンプルは「bluesky」に貼ったときの画像レイアウトを表示しています。
                    </div>
                </div>
                <div class="mb-3">
                    <label class="caption">表紙</label>
                    <div class="my-3" v-if="file.url">
                        <img :src="file.url" class="img-thumbnail cover-img" />
                    </div>
                    <div class="btn-group file-selector">
                        <label class="btn btn-secondary" style="margin: 0;" for="cover">
                            <i class="fas fa-file-image"></i>
                            表紙画像選択...
                            <input type="file" id="cover" ref="preview" @change="fileSelected()" accept="image/*" class="d-none" />
                        </label>
                        <button type="button" @click="removeImage" class="btn btn-light">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="small">
                        <ul>
                            <li>未設定の場合は画像は表示されません。</li>
                            <li>投稿できるサイズは5MBまでです。</li>
                            <li>推奨される画像サイズは幅500px 高さ705px以上です（1:1.41）。</li>
                            <li>上記アスペクト比以外の画像は、トリミングされます。</li>
                            <li>
                                <a href="https://sscard.monokakitools.net/covermaker.html" target="_blank">
                                    かんたん表紙メーカー
                                    <i class="fas fa-external-link-alt"></i>
                                </a>
                                は、推奨サイズの表紙画像を素早く作成できます。
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="mb-3">
                    <label class="caption">リンクバナー</label>
                    <div class="my-3" v-if="link.url">
                        <img :src="link.url" class="img-thumbnail img-link mb-2" />
                        <div class="input-group" v-if="link.url.match(/^http/)">
                            <input type="text" :value="link.url" class="form-control form-control-sm" readonly>
                            <div class="input-group-append">
                                <button class="btn btn-outline-secondary btn-sm" @click="copy(link.url)">
                                    <i class="far fa-copy"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="btn-group file-selector">
                        <label class="btn btn-secondary" style="margin: 0;" for="link">
                            <i class="fas fa-file-image"></i>
                            リンクバナー選択...
                            <input type="file" id="link" ref="link" @change="linkSelected()" accept="image/*" class="d-none" />
                        </label>
                        <button type="button" @click="removeLink" class="btn btn-light">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="small">
                        <ul>
                            <li>200 x 40サイズのリンク専用バナーです。</li>
                            <li>画像サイズは幅200px 高さ40pxです。</li>
                            <li>サーチサービスへの登録にご利用ください。</li>
                            <li>著作権を侵害する画像、公序良俗に反する画像は指定できません。</li>
                        </ul>
                    </div>
                </div>
                <div class="mb-3" v-if="book.id && book.is_long && book.sections_count">
                    <label class="caption">連載中・完結</label>
                    <div>
                        <label class="btn btn-radio" :class="{
                            'btn-primary': book.is_finished == 0,
                            'btn-outline-primary': book.is_finished != 0,
                        }">
                            <input type="radio" v-model="book.is_finished" :value="0" class="d-none" />
                            <radio :value="book.is_finished === 0"></radio>
                            <i class="fas fa-running"></i>
                            連載中
                        </label>
                        <label class="btn btn-radio" :class="{
                            'btn-danger': book.is_finished == 1,
                            'btn-outline-danger': book.is_finished != 1,
                        }">
                            <input type="radio" v-model="book.is_finished" :value="1" class="d-none" />
                            <radio :value="book.is_finished === 1"></radio>
                            <i class="fas fa-flag-checkered"></i>
                            完結
                        </label>
                    </div>
                    <div class="text-muted small">
                        完結にすると、最終話公開時に自動的に完結作品となります。
                        最終話が公開されるまでは「連載中」になります。<br>
                        最終話を公開せずに下書きのままにしておくと、永久に完結しませんのでご注意ください。
                    </div>
                </div>
                <div class="mb-3">
                    <label class="caption">電子書籍</label>
                    <div>
                        <label class="btn btn-radio" :class="{
                            'btn-primary': book.has_epub == 1,
                            'btn-outline-primary': book.has_epub != 1,
                        }">
                            <input type="radio" v-model="book.has_epub" :value="1" class="d-none" />
                            <radio :value="book.has_epub === 1"></radio>
                            <i class="fas fa-book-open"></i>
                            出力する
                        </label>
                        <label class="btn btn-radio" :class="{
                            'btn-secondary': book.has_epub == 0,
                            'btn-outline-secondary': book.has_epub != 0,
                        }">
                            <input type="radio" v-model="book.has_epub" :value="0" class="d-none" />
                            <radio :value="book.has_epub === 0"></radio>
                            <i class="fas fa-times"></i>
                            出力しない
                        </label>
                    </div>
                    <div class="text-muted small">
                        完結時に電子書籍のダウンロードを有効にするかを設定できます。
                    </div>
                </div>
            </div>
            <div class="form-group text-center save-button">
                <button type="button" class="btn btn-outline-primary w-50" @click="save()">
                    <i class="fas fa-save"></i>
                    保存
                </button>
            </div>
            <div class="form-group text-center save-button" v-if="book.id">
                <button type="button" class="btn btn-outline-danger w-50" @click="deleteBook()">
                    <i class="fas fa-trash-alt"></i>
                    削除
                </button>
            </div>
            <div class="save-button text-center">
                <router-link to="/mypage/books">戻る</router-link>
            </div>
        </div>
        <loading
            v-model:active="isLoading"
            :can-cancel="false"
            :is-full-page="true"></loading>
    </div>
</template>

<style scoped lang="scss">
.btn-radio {
    margin: .5em 1em .5em 0;
}
.strong {
    font-weight: bold;
    text-decoration: underline;
}
.save-button {
    margin-top: 4rem;
}
.cover-img {
    width: 250px;
    height: 352px;
    object-fit: cover;
}
.img-link {
    width: calc(200px + 0.5rem + 2px);
    height: calc(40px + 0.5rem + 2px);
}
.twitter-img {
    width: 509px;
    height: 267px;
    object-fit: cover;
}
.file-selector {
    margin: 1em 0;
}
.caption {
    display: block;
    padding: 1rem 0 0;
    font-weight: bold;
    border-bottom: 1px solid #ddd;
    &.no-border {
        border-bottom: 0;
    }
}
.chars {
    padding: .5em 0 0;
    margin: .5em;
    font-size: .7rem;
    color: #aaa;
}
.badge-tag {
    padding: .7em 1em;
    font-size: 1rem;
    margin: 0 1em .5em 0;
    .close {
        margin: 0 0 0 .7em;
        font-size: 1rem;
    }
}
.description {
    border-radius: .5rem;
    padding: .8rem;
    margin: .8rem 0;
    max-width: 640px;
    border: 1px solid #ddd;
}
.text-pane {
    position: relative;
    letter-spacing: 1px;
    border: 0px;
    border-radius: 4px;
    margin: 0 auto .5rem;
    .text-pane-dummy {
        overflow: hidden;
        font-size: .85rem;
        line-height: 1.8rem;
        box-sizing: border-box;
        padding: .5rem 1rem;
        min-height: 120px;
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
        letter-spacing: 1px;
        border: 0;
        border-radius: 4px;
        opacity: 0;
    }
    .text-pane-editor {
        position: absolute;
        top: 0;
        left: 0;
        font-size: .85rem;
        line-height: 1.8rem;
        letter-spacing: 1px;
        display: block;
        overflow: hidden;
        box-sizing: border-box;
        padding: .5rem 1rem;
        width: 100%;
        height: 100%;
        border: 0;
        resize: none;
        background-color: #aaaaaa20;
        &:focus {
            outline: none;
        }
        border-radius: 4px;
    }
}
@media screen and (max-width: 770px) {
    .container {
        padding: 0;
    }
}
</style>

<script>
import check from "components/Check";
import radio from "components/Radio";
export default {
    components: {
        check,
        radio,
    },
    data() {
        return {
            book: {
                rating_id: null,
                tags: [],
                title: "",
                subtitle: "",
                subdomain: "",
                is_hidden: 0,
                is_backup: 0,
                is_finished: 0,
                card_type: 0,
            },
            genres: [],
            ratings: [],
            tag: [],
            series: [],
            isLoading: false,
            details: false,
            file: {
                info: null,
                url: null,
            },
            link: {
                info: null,
                url: null,
            },
            mytags: [],
            errors: {},
            err: null,
            res: null,
            timeoutID: null,
        }
    },
    methods: {
        load() {
            if (! isFinite(this.$route.params.book_id)) return;
            this.details = true;
            this.isLoading = true;
            axios.get("/mypage/book/get", {params: {id: this.$route.params.book_id}}).then(res => {
                this.book = res.data;
                if (this.book.cover_url) {
                    this.file.url = this.book.cover_url + "?" + this.book.cover_hash;
                }
                if (this.book.banner) {
                    this.link.url = this.book.banner.picture.url + '?' + this.book.banner.picture.hash;
                }
            }).catch(err => {
                console.error(err);
                alert.err(err.response);
            }).finally(() => {
                this.isLoading = false;
            });
        },
        save() {
            this.isLoading = true;
            axios.post("/mypage/book/save", this.book).then(res => {
                // 表紙画像アップロード（空は削除）
                const fd = new FormData();
                const id = res.data;
                fd.append('cover', this.file.info);
                fd.append('url', this.file.url);
                fd.append('link', this.link.info);
                fd.append('link_url', this.link.url);
                fd.append('id', id);
                axios.post("/mypage/book/upload", fd).then(res => {
                    if (! this.book.id) {
                        if (this.book.is_long) {
                            this.$router.push("/mypage/book/" + id + "/plot");
                        } else {
                            this.$router.push("/mypage/book/" + id + "/section/edit/new");
                        }
                    } else {
                        this.$router.push("/mypage/book/" + id + "/sections/");
                    }
                }).catch(err => {
                    alert.err(err.response);
                    if (! this.book.id) {
                        // 新規でアップロード失敗したらリストに戻す
                        this.$router.push("/mypage/books");
                    }
                }).finally(() => {
                    this.isLoading = false;
                });
                alert.msg("書籍情報を保存しました。");
            }).catch(err => {
                this.isLoading = false;
                if (err?.response?.data?.errors) {
                    this.errors = err?.response?.data?.errors;
                }
                alert.err(err.response);
            });
        },
        deleteBook() {
            const styled = Swal.mixin({
                customClass: {
                    confirmButton: "btn btn-danger btn-dialog",
                    cancelButton: "btn btn-secondary btn-dialog",
                },
                buttonsStyling: false
            });
            styled.fire({
                icon: "error",
                title: "小説削除",
                html: `<div class='text-left'>この小説を削除します。<br>
                <b>削除された小説は元に戻せません。</b><br>
                間違いを防ぐため、以下に小説のタイトルを入力してください。</div>`,
                input: "text",
                showCancelButton: true,
                confirmButtonText: "削除実行",
                cancelButtonText: "キャンセル",
                reverseButtons: true
            }).then(confirm => {
                if (confirm.isConfirmed) {
                    if (confirm.value !== this.book.title) {
                        alert.err("入力されたタイトルが間違っています。");
                        return;
                    }
                    axios.post("/mypage/book/delete", {id: this.book.id}).then(res => {
                        alert.msg(res.data);
                        this.$router.push("/mypage/books");
                    }).catch(err => {
                        alert.err(err.response);
                    });
                }
            });
        },
        fileSelected() {
            if (this.$refs.preview.files[0].size > 5000 * 1024) {
                alert.err("ファイルサイズが大きすぎます: " + this.getSize(this.$refs.preview.files[0].size));
                return;
            }
            this.file.info = this.$refs.preview.files[0];
            this.file.url = URL.createObjectURL(this.file.info);
        },
        linkSelected() {
            if (this.$refs.link.files[0].size > 500 * 1024) {
                alert.err("ファイルサイズが大きすぎます: " + this.getSize(this.$refs.link.files[0].size));
                return;
            }
            this.link.info = this.$refs.link.files[0];
            this.link.url = URL.createObjectURL(this.link.info);
        },
        removeImage() {
            this.file.info = null;
            this.file.url = "";
        },
        removeLink() {
            this.link.info = null;
            this.link.url = "";
        },
        getSize(filesize) {
            if (filesize >= 1073741824) {
                return Math.round(filesize / 1073741824 * 100) / 100 + "GB";
            } else if (filesize >= 1048576) {
                return Math.round(filesize / 1048576 * 100) / 100 + "MB";
            } else if (filesize >= 1024) {
                return Math.round(filesize / 1024 * 100) / 100 + "KB";
            }
            return filesize + "B";
        },
        addTag(ev = null) {
            if (ev && ev.keyCode !== 13) return;
            if (! this.book.tags || this.book.tags.length >= 15) return;
            if (! _.find(this.book.tags, {tag_name: this.tag})) {
                this.book.tags.push({
                    tag_name: this.tag
                });
                this.tag = null;
            }
        },
        appendTag(t) {
            if (! _.find(this.book.tags, {tag_name: t.tag_name})) {
                this.book.tags.push({
                    tag_name: t.tag_name
                });
                this.mytags = _.reject(this.mytags, {id: t.id});
            }
        },
        removeTag(i) {
            this.book.tags.splice(i, 1);
        },
        loadGenres() {
            axios.get("/mypage/genres/get?no_hondana=1").then(res => {
                this.genres = res.data;
            }).catch(err => {
                alert.err(err.response);
            });
        },
        copy(url) {
            navigator.clipboard.writeText(url).then(() => {
                alert.msg("URLをコピーしました。");
            }).catch(err => { alert.err(err) });
        }
    },
    computed: {
        selectedGenre() {
            if (this.genres.length == 0) return "";
            let genre = _.find(this.genres, {id: this.book.genre_id});
            if (genre) {
                return genre.description;
            }
            return "";
        },
        selectedSeries() {
            if (this.series.length == 0) return "";
            let series = _.find(this.series, {id: this.book.series_id});
            if (series) {
                return series.description;
            }
            return "";
        },
        isShortable() {
            if (this.book.sections_count) {
                return this.book.sections_count <= 1;
            } else {
                return true;
            }
        },
        subtitleLength() {
            return this.book?.subtitle?.length || 0;
        },
        twitterCard() {
            let url = "";
            if (this.book.card_type == 0 && this.file.url) {
                url = this.file.url;
            } else {
                url = "//" + (this.$store.state.user.subdomain || "www") + this.$store.state.base_domain + "/card/" + this.book.id + "?card_type=" + this.book.card_type;
            }
            return url;
        },
        booksPath() {
            if (this.book.is_hidden === 3) {
                return "/mypage/books/pending";
            } else if (this.book?.property?.is_finished) {
                return "/mypage/books/finished";
            }
            return "/mypage/books";
        },
        overRated() {
            if (!this.$store.state.user || !this.ratings) return false;
            const user_rating = _.find(this.ratings, {id: this.$store.state.user.rating_id});
            const novel_rating = _.find(this.ratings, {id: this.book.rating_id});
            if (!this.$store.state.user.rating_id && novel_rating && novel_rating.rating_level) {
                return true;
            }
            if (!user_rating || !novel_rating) return false;
            if (user_rating.rating_level < novel_rating.rating_level) {
                return true;
            }
            return false;
        },
        hasDomain() {
            return this.$store.state.user && this.$store.state.user.subdomain;
        },
        hasVerifiedEmail() {
            return this.$store.state.user && this.$store.state.user.email_verified_at && !this.$store.state.user.is_prohibited;
        }
    },
    mounted() {
        // マスタ読み込み
        axios.get("/master/ratings").then(res => {
            this.ratings = res.data;
        }).catch(err => {
            alert.err(err.response);
        });
        axios.get("/mypage/mytags/get", {params: {except: this.$route.params.book_id}}).then(res => {
            this.mytags = res.data;
        }).catch(err => {
            alert.err(err.response);
        });
        axios.get("/mypage/series/get").then(res => {
            this.series = res.data;
        }).catch(err => { alert.err(err.response) });
        this.loadGenres();
        this.load();
    }
}
</script>